import React, { FC, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router';

import cn from 'classnames';
import GetEmail from 'components/Auth/GetEmail';
import GetPassword from 'components/Auth/GetPassword';
import GetStarted from 'components/Auth/GetStarted';
import ImportMyDataConsent from 'components/Auth/ImportMyDataConsent';
import LmsApiAuth from 'components/Auth/LmsApiAuth';
import SetPassword from 'components/Auth/SetPassword';
import VerifyCode from 'components/Auth/VerifyCode';
import { AuthScreens } from 'globals/constants';
import { RootState } from 'redux/reducers';
import { ROUTES } from 'routes';

import useAuthStateManager, { AuthActions } from './AuthStateManager';

interface AuthModuleProps {
  isAuthorized: boolean;
  accessData: SessionData | null;
}

const AuthModule: FC<AuthModuleProps> = ({ accessData, isAuthorized }) => {
  const { state, dispatch: setAuthState } = useAuthStateManager();
  const year = useMemo(() => new Date().getFullYear(), []);

  useEffect(() => {
    if (!accessData || !accessData.access) return;

    if (!accessData.isImported) {
      return setAuthState({
        type: AuthActions.SET_SCREEN,
        payload: AuthScreens.IMPORT_DATA,
      });
    }

    if (!accessData.isLinked) {
      return setAuthState({
        type: AuthActions.SET_SCREEN,
        payload: AuthScreens.GET_STARTED,
      });
    }

    if (!accessData.passwordSet) {
      return setAuthState({
        type: AuthActions.GOTO_SET_PASSWORD,
        payload: { isResettingPassword: false },
      });
    }

    return setAuthState({
      type: AuthActions.SET_SCREEN,
      payload: AuthScreens.GET_PASSWORD,
    });
  }, [accessData, setAuthState]);

  if (!accessData) {
    return (
      <Redirect
        to={{
          pathname: ROUTES.MESSAGE_PAGE,
          state: { code: '403', message: 'You are not authorised to access this app.' },
        }}
      />
    );
  }

  if (accessData.courseApprovalRequired) {
    return <Redirect to={ROUTES.COURSE_APPROVAL} />;
  }

  if (!accessData.access) {
    return (
      <Redirect
        to={{
          pathname: ROUTES.MESSAGE_PAGE,
          state: { code: '403', message: accessData.errorMessage },
        }}
      />
    );
  }

  if (isAuthorized) {
    switch (accessData.context) {
      case 'course': {
        if (accessData.courseDetails.isImported) {
          return <Redirect to={ROUTES.COURSES} />;
        }
        return <Redirect to={ROUTES.COURSES_CREATE} />;
      }
      case 'organization': {
        return <Redirect to={ROUTES.ADMIN} />;
      }
    }
  }

  let body: JSX.Element = <></>;

  switch (state.screen) {
    case AuthScreens.IMPORT_DATA:
      body = <ImportMyDataConsent setAuthState={setAuthState} />;
      break;
    case AuthScreens.GET_STARTED:
      body = <GetStarted setAuthState={setAuthState} />;
      break;
    case AuthScreens.GET_EMAIL:
      body = <GetEmail setAuthState={setAuthState} />;
      break;
    case AuthScreens.VERIFY_CODE:
      body = (
        <VerifyCode
          linkToken={state.linkPasswordResponse?.linkToken || ''}
          context={state.verifyCodeContext}
          setAuthState={setAuthState}
        />
      );
      break;
    case AuthScreens.SET_PASSWORD:
      body = <SetPassword isResetting={state.isResettingPassword} setAuthState={setAuthState} />;
      break;
    case AuthScreens.GET_PASSWORD:
      body = (
        <GetPassword
          isLinking={state.verifyCodeContext === 'link'}
          userId={state.linkEmailResponse?.userId || ''}
          cluster={state.linkEmailResponse?.cluster || ''}
          setAuthState={setAuthState}
        />
      );
      break;
    case AuthScreens.LMS_AUTHORIZATION:
      body = <LmsApiAuth redirectUrl={state.authResponse?.lmsAuthUrl || ''} />;
      break;
    default:
      body = <></>;
      break;
  }

  return (
    <div className={cn('page', 'auth')}>
      <div className='auth__container'>
        {body}
        <div className='auth__footer'>&#9400; {year}, Acadly Inc.</div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accessData: state.getAccess,
    isAuthorized: state.isAuthorized,
  };
};

export default connect(mapStateToProps)(AuthModule);
